import * as React from "react";
import { connect } from "react-redux";
import { Button, Form, Modal } from "react-bootstrap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { Document, Page, pdfjs } from "react-pdf";
import { jsPDF } from "jspdf";
import ScrollContainer from "react-indiana-drag-scroll";
import EllipsisWithTooltip from "react-ellipsis-with-tooltip";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { InvoiceService } from "../../Common/Services/InvoiceService";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

class ImportInvoicePdf extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      status: "initial",
      fileName: "",
      selectedFile: null,
      isExtracting: false,
      numPages: 0,
      selectedPages: [] as number[],
      error: false,
    };
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.showModal && !this.props.showModal) {
      this.resetState();
    }
  }

  resetState = () => {
    this.setState({
      status: "initial",
      fileName: "",
      selectedFile: null,
      isExtracting: false,
      numPages: 0,
      selectedPages: [],
      error: false,
    });
  };

  closeModal = () => {
    this.resetState();
    this.props.onClose?.();
  };

  handleCancelPrompt = () => {
    this.resetState();
  };

  handleFileSelect = (files: File[]) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        containerId: "OrderSlideout",
      });
      return;
    }

    this.setState({
      status: "viewer",
      fileName: file.name,
      selectedFile: file,
      selectedPages: [],
      numPages: 0,
      error: false,
    });
  };

  onDrop = (acceptedFiles: File[]) => {
    this.handleFileSelect(acceptedFiles);
  };

  handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    this.handleFileSelect(files);
    event.target.value = "";
  };

  onDocumentLoadSuccess = ({ numPages }: any) => {
    this.setState((prevState: any) => ({
      numPages,
      selectedPages: prevState.selectedPages,
    }));
  };

  renderError = () => {
    this.setState({ error: true });
  };

  togglePage = (pageNumber: number) => {
    this.setState((prevState: any) => {
      const exists = prevState.selectedPages.includes(pageNumber);
      return {
        selectedPages: exists
          ? prevState.selectedPages.filter((page: number) => page !== pageNumber)
          : [...prevState.selectedPages, pageNumber],
      };
    });
  };

  buildSelectedPagesPdf = async (sourceFile: File, selectedPages: number[]) => {
    const arrayBuffer = await sourceFile.arrayBuffer();
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdfDocument = await loadingTask.promise;
    let outputDoc: any = null;

    for (let i = 0; i < selectedPages.length; i++) {
      const pageNumber = selectedPages[i];
      const page = await pdfDocument.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Unable to render selected PDF pages.");
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      const imageData = canvas.toDataURL("image/jpeg", 1.0);
      const orientation = viewport.width >= viewport.height ? "landscape" : "portrait";
      const pageWidth = Math.ceil(viewport.width);
      const pageHeight = Math.ceil(viewport.height);

      if (!outputDoc) {
        outputDoc = new jsPDF({
          orientation,
          unit: "px",
          format: [pageWidth, pageHeight],
        });
      } else {
        outputDoc.addPage([pageWidth, pageHeight], orientation);
      }

      outputDoc.setPage(i + 1);
      outputDoc.addImage(imageData, "JPEG", 0, 0, pageWidth, pageHeight);
    }

    const pdfBlob = outputDoc.output("blob");
    const fileName = sourceFile.name || "invoice.pdf";
    return new File([pdfBlob], fileName, {
      type: "application/pdf",
    });
  };

  handleExtract = async () => {
    const { selectedFile, selectedPages, numPages } = this.state;

    if (!selectedFile) {
      toast.error("Please select a PDF file.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        containerId: "OrderSlideout",
      });
      return;
    }

    const pagesToExtract =
      selectedPages.length > 0 ? [...selectedPages].sort((a, b) => a - b) : [];

    if (numPages > 1 && pagesToExtract.length === 0) {
      toast.error("Please select at least one page to extract.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        containerId: "OrderSlideout",
      });
      return;
    }

    this.setState({ status: "loading", isExtracting: true });

    try {
      const extractionFile =
        numPages > 1 && pagesToExtract.length < numPages
          ? await this.buildSelectedPagesPdf(selectedFile, pagesToExtract)
          : selectedFile;

      const response:any = await InvoiceService.ExtractInvoice({
        file: extractionFile,
        originalFile: selectedFile,
      });
    
     const result = response.result;

      if (typeof result === "string") {
        this.setState({ status: "viewer" });
        toast.error(result, {
          position: toast.POSITION.BOTTOM_RIGHT,
          containerId: "OrderSlideout",
        });
        return;
      }

      if (result) {
        toast.success("PDF extracted successfully and saved as draft.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          containerId: "OrderSlideout",
        });
        this.props.onExtracted?.(result);
        this.closeModal();
      } else {
        this.setState({ status: "viewer" });
        toast.error("No extraction data was returned.", {
          position: toast.POSITION.BOTTOM_RIGHT,
          containerId: "OrderSlideout",
        });
      }
    } catch (err: any) {
      console.log("Extraction error:", err);
      this.setState({ status: "viewer" });
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Server Error",
        {
          position: toast.POSITION.BOTTOM_RIGHT,
          containerId: "OrderSlideout",
        }
      );
    } finally {
      this.setState({ isExtracting: false });
    }
  };

  render() {
    const { showModal } = this.props;
    const { status, fileName, numPages, selectedPages, error, selectedFile } = this.state;

    return (
      <Modal
        show={showModal}
        onHide={this.closeModal}
        className="attach-modal"
        dialogClassName="pdf-import-dialog"
        centered
        size="lg"
      >
        <style>{`
          .pdf-import-dialog {
            width: 100%;
            max-width: 1400px;
            margin: 1.75rem auto;
          }
          .pdf-import-dialog .modal-content {
            border-radius: 7px;
            overflow: hidden;
            box-shadow: 0 18px 40px rgba(12, 34, 70, 0.18);
            height: 90vh;
            max-height: 90vh;
          }
          .pdf-import-dialog .modal-body {
            padding: 0;
            height: calc(90vh - 132px);
            display: flex;
            flex-direction: column;
          }
          .pdf-view-wrap {
            display: flex;
            flex: 1;
            min-height: 0;
            background: #f7f9fc;
            overflow: hidden;
          }
          .pdf-sidebar {
            width: 280px;
            border-right: 1px solid #d9e3f2;
            background: linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%);
            padding: 1.25rem 1rem;
            overflow: auto;
          }
          .pdf-stage {
            flex: 1;
            min-width: 0;
            padding: 1rem;
            overflow: auto;
          }
          .pdf-page-list {
            max-height: calc(90vh - 220px);
            overflow: auto;
            padding-right: 0.25rem;
          }
          .pdf-page-card {
            background: #fff;
            border: 1px solid #d8e2f1;
            border-radius: 10px;
            margin-bottom: 0.75rem;
            overflow: hidden;
            box-shadow: 0 10px 22px rgba(22, 77, 160, 0.08);
          }
          .pdf-page-label {
            padding: 0.7rem 0.8rem;
            border-top: 1px solid #edf2fa;
            background: #fbfdff;
          }
          .pdf-scan-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1.75rem 2.25rem;
            gap: 1rem;
          }
          .pdf-paper {
            position: relative;
            width: 86px;
            height: 108px;
            border-radius: 12px;
            border: 2px solid #b9cbe7;
            background: linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%);
            box-shadow: 0 8px 20px rgba(22, 77, 160, 0.12);
            overflow: hidden;
          }
          .pdf-paper::before {
            content: "";
            position: absolute;
            left: 12px;
            right: 12px;
            top: 18px;
            height: 8px;
            border-radius: 999px;
            background: #dfe8f6;
            box-shadow: 0 18px 0 #dfe8f6, 0 36px 0 #dfe8f6, 0 54px 0 #dfe8f6;
            opacity: 0.95;
          }
          .pdf-scan-line {
            position: absolute;
            left: 8px;
            right: 8px;
            top: 8px;
            height: 4px;
            border-radius: 999px;
            background: linear-gradient(90deg, rgba(22,77,160,0.15), rgba(22,77,160,1), rgba(22,77,160,0.15));
            box-shadow: 0 0 18px rgba(22,77,160,0.45);
            animation: pdfscan 1.35s ease-in-out infinite;
          }
          @keyframes pdfscan {
            0% { transform: translateY(0); opacity: 0.35; }
            50% { transform: translateY(80px); opacity: 1; }
            100% { transform: translateY(0); opacity: 0.35; }
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>Import PDF</Modal.Title>
          {fileName && <div className="px-4 mt-1 text-muted">{fileName}</div>}
        </Modal.Header>
        <Modal.Body>
          {status === "initial" && (
            <div className="filter-sec upload-section d-flex justify-content-center align-items-center" style={{ minHeight: "100%" }}>
              <Form.Group className="w-100" style={{ maxWidth: "620px" }}>
                <div className="mt-1 py-5 rounded-3 text-center text-secondary" style={{ border: "solid 1px #c0c0c0" }}>
                  <section className="container upload-area default">
                    <Dropzone onDrop={this.onDrop} multiple={false} accept="application/pdf">
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <div {...getRootProps({ className: "dropzone align-items-center" })}>
                          <input {...getInputProps()} />
                          <p>
                            Drag and drop a PDF or <span>browse files</span> to upload.
                          </p>
                          {isDragActive && <p>Drop the PDF here ...</p>}
                        </div>
                      )}
                    </Dropzone>
                  </section>
                </div>
              </Form.Group>
            </div>
          )}

          {status === "viewer" && selectedFile && (
            <div className="pdf-view-wrap">
              <div className="pdf-sidebar">
                <div className="fw-semibold mb-1" style={{ color: "#164DA0" }}>
                  Select Pages
                </div>
                <div className="text-muted small mb-3">
                  Choose the pages you want to send for extraction.
                </div>
                <div className="pdf-page-list">
                  {Array.from({ length: numPages || 0 }, (_x, index) => {
                    const pageNumber = index + 1;
                    const checked = selectedPages.includes(pageNumber);
                    return (
                      <div key={pageNumber} className="pdf-page-card">
                        <div className="pdf-page-label">
                          <Form.Check
                            type="checkbox"
                            id={`page_${pageNumber}`}
                            label={`Page ${pageNumber}`}
                            checked={checked}
                            onChange={() => this.togglePage(pageNumber)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="pdf-stage">
                <Document
                  file={selectedFile}
                  onLoadSuccess={this.onDocumentLoadSuccess}
                  loading={""}
                  error={this.renderError}
                >
                  {Array.from({ length: numPages || 0 }, (_x, index) => (
                    <div key={`preview_${index}`} className="mb-4 bg-white rounded-3 p-3 shadow-sm">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-semibold">
                          <EllipsisWithTooltip placement="bottom">{fileName}</EllipsisWithTooltip>
                        </div>
                        <div className="text-muted small">Page {index + 1} of {numPages}</div>
                      </div>
                      <ScrollContainer className="w-100" hideScrollbars={false}>
                        <Page
                          pageNumber={index + 1}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          scale={1.2}
                        />
                      </ScrollContainer>
                    </div>
                  ))}
                </Document>
                {error && (
                  <div className="custom-error-msg">Cannot be previewed - {fileName}</div>
                )}
              </div>
            </div>
          )}

          {status === "loading" && (
            <div className="pdf-scan-wrap text-center">
              <div className="pdf-paper" aria-hidden="true">
                <div className="pdf-scan-line" />
              </div>
              <div className="fw-semibold" style={{ color: "#164DA0", fontSize: "18px" }}>
                Extracting file data
              </div>
              <div className="text-secondary">{fileName}</div>
              <div className="text-secondary" style={{ maxWidth: "420px" }}>
                Please wait while we scan and extract the invoice details from your PDF.
              </div>
            </div>
          )}
        </Modal.Body>
        {status === "viewer" && (
          <Modal.Footer className="justify-content-center gap-3 border-0 pt-0 pb-4">
            <Button type="button" variant="secondary" onClick={this.handleCancelPrompt} disabled={this.state.isExtracting}>
              Cancel
            </Button>
            <Button type="button" onClick={this.handleExtract} disabled={!this.state.selectedFile || this.state.isExtracting || status !== "viewer"}>
              {this.state.isExtracting ? "Extracting..." : `Extract${selectedPages.length > 0 ? ` (${selectedPages.length})` : ""}`}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    );
  }
}

const mapStateToProps = (state: any) => ({
  locationId: state.LocationReducer.locationId,
});

export default connect(mapStateToProps)(ImportInvoicePdf);
